
/**
 * PostProcessManager - orchestrates post-processing effects for both WebGL and WebGPU.
 * Delegates to renderer-specific pipelines with unified effect configuration API.
 */

import * as THREE from 'three';

import { ObjectTracker } from '../systems/ObjectTracker.js';

import { AntiAliasingEffect } from './postprocessing/effects/AntiAliasingEffect.js';
import { AOEffect } from './postprocessing/effects/AOEffect.js';
import { BloomEffect } from './postprocessing/effects/BloomEffect.js';
import { ColorGradingEffect } from './postprocessing/effects/ColorGradingEffect.js';
import { DepthOfFieldEffect } from './postprocessing/effects/DepthOfFieldEffect.js';
import { ObjectOutlineEffect } from './postprocessing/effects/ObjectOutlineEffect.js';
import { PixelationEffect } from './postprocessing/effects/PixelationEffect.js';
import { RetroEffect } from './postprocessing/effects/RetroEffect.js';
import { SSREffect } from './postprocessing/effects/SSREffect.js';
import { ToneMappingEffect } from './postprocessing/effects/ToneMappingEffect.js';
import { type BaseEffectConfig } from './postprocessing/IPostProcessEffect.js';
import { WebGLPipeline } from './postprocessing/pipelines/WebGLPipeline.js';
import { WebGPUPipeline } from './postprocessing/pipelines/WebGPUPipeline.js';

import type { IPostProcessEffect } from './postprocessing/IPostProcessEffect.js';
import type { IPostProcessPipeline } from './postprocessing/IPostProcessPipeline.js';
import type { PostProcessConfiguration } from './postprocessing/PostProcessConfig.js';
import type { IGenesysRenderer } from './Renderer.js';
import type { Actor } from '../actors/Actor.js';

// Re-export for convenience (IPostProcessEffect as value so consumers can extend it)
export type { BaseEffectConfig } from './postprocessing/IPostProcessEffect.js';
export { IPostProcessEffect } from './postprocessing/IPostProcessEffect.js';
export type { IPostProcessPipeline } from './postprocessing/IPostProcessPipeline.js';
export * from './postprocessing/PostProcessConfig.js';

/** Version number for PostProcessManager - increment when making breaking changes */
export const POST_PROCESS_MANAGER_VERSION = 4;

/**
 * Legacy effect type aliases for backward compatibility.
 * Maps old/legacy type names to canonical PostProcessPass types.
 */
const EffectTypeAliases: Record<string, string> = {
  ssao: 'ao', // SSAO was renamed to unified AO effect
};

/**
 * Normalise effect type from legacy aliases to canonical types.
 */
function normaliseEffectType(type: string): string {
  return EffectTypeAliases[type] ?? type;
}

function getEffectConfigKey(effect: Pick<BaseEffectConfig, 'id' | 'type'>): string {
  return effect.id ?? effect.type;
}

function normaliseEffectConfig(effect: BaseEffectConfig): BaseEffectConfig {
  const type = normaliseEffectType(effect.type);
  return {
    ...effect,
    id: effect.id ?? type,
    type,
  };
}

type BeforeRenderHook = (
  renderer: IGenesysRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  windowSize: { width: number; height: number }
) => void;

/**
 * PostProcessManager orchestrates post-processing effects for rendering.
 *
 * This is a simplified manager that delegates to renderer-specific pipelines
 * (WebGLPipeline or WebGPUPipeline) which handle the actual effect composition.
 *
 * Features:
 * - Unified API for both WebGL and WebGPU
 * - Plugin-based effect system
 * - External extensibility via effect registration
 * - Delegate-based hooks for customization
 */
export class PostProcessManager {
  protected pipeline: IPostProcessPipeline | null = null;
  protected lastRenderer: IGenesysRenderer | null = null;
  protected enabled: boolean = true;
  protected destroyed = false;
  protected beforeRenderHook: BeforeRenderHook | null = null;

  constructor(config?: PostProcessConfiguration) {
    ObjectTracker.track(this);
    // Effects will be registered when pipeline is created
    if (config) {
      // Store config for later when pipeline is created
      this.pendingConfig = config;
    }
  }

  protected pendingConfig?: PostProcessConfiguration;

  /**
   * Register a custom post-processing effect.
   * This allows external code (like the Editor) to add custom effects.
   */
  public registerEffect(effect: IPostProcessEffect): void {
    this.pipeline?.registerEffect(effect);
  }

  /**
   * Register an effect class by constructing it.
   * Useful for plugin-driven dynamic effect registration.
   */
  public registerEffectClass<T extends IPostProcessEffect>(EffectClass: new () => T): void {
    this.registerEffect(new EffectClass());
  }

  /**
   * Unregister a custom effect.
   */
  public unregisterEffect(type: string): void {
    const normalisedType = normaliseEffectType(type);
    this.pipeline?.unregisterEffect(normalisedType);
  }

  /**
   * Get a registered effect by type.
   */
  public getEffect(type: string): IPostProcessEffect | null {
    const normalisedType = normaliseEffectType(type);
    return this.pipeline?.getEffect(normalisedType) ?? null;
  }

  /**
   * Get all registered effects.
   */
  public getAllEffects(): ReadonlyArray<IPostProcessEffect> {
    return this.pipeline?.getAllEffects() ?? [];
  }

  /**
   * Configure a specific effect.
   */
  public configureEffect(type: string, config: Partial<BaseEffectConfig> & Record<string, unknown>): void {
    const resolvedType = this.resolveEffectType(type);
    const normalisedConfig = normaliseEffectConfig({
      ...(config as BaseEffectConfig),
      type: resolvedType,
    });

    // If pipeline not ready yet, buffer into pending config
    if (!this.pipeline) {
      this.mergePendingConfig({ effects: [normalisedConfig] });
      return;
    }

    this.pipeline.configureEffect(resolvedType, normalisedConfig as Partial<BaseEffectConfig> & Record<string, unknown>);
  }

  /**
   * Get current configuration for an effect.
   */
  public getEffectConfig(type: string): BaseEffectConfig | null {
    const resolvedType = this.resolveEffectType(type);

    // Check pipeline first
    if (this.pipeline) {
      return this.pipeline.getEffectConfig(resolvedType);
    }
    // Fall back to pending config if pipeline not initialized
    if (this.pendingConfig) {
      const config = this.pendingConfig.effects.find(effect => getEffectConfigKey(effect) === type || effect.type === resolvedType);
      return config ?? null;
    }
    return null;
  }

  /**
   * Set multiple effect configurations at once.
   */
  public configure(config: PostProcessConfiguration): void {
    const normalisedConfig: PostProcessConfiguration = {
      effects: config.effects.map(effectConfig => normaliseEffectConfig(effectConfig)),
    };

    // If pipeline not ready yet, buffer into pending config
    if (!this.pipeline) {
      this.mergePendingConfig(normalisedConfig);
      return;
    }

    const orderedEffects = [...normalisedConfig.effects].sort((a, b) => {
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    for (const effectConfig of orderedEffects) {
      const resolvedType = this.resolveEffectType(effectConfig.id ?? effectConfig.type);
      this.pipeline.configureEffect(resolvedType, { ...effectConfig, type: resolvedType } as Partial<BaseEffectConfig> & Record<string, unknown>);
    }
  }

  /**
   * Set post-processing configuration (merges with defaults).
   */
  public setConfiguration(config: PostProcessConfiguration): void {
    this.configure(config);
  }

  /**
   * Update post-processing configuration (merges with current).
   */
  public updateConfiguration(config: PostProcessConfiguration): void {
    this.configure(config);
  }

  /**
   * Get all current configurations.
   */
  public getAllConfigurations(): PostProcessConfiguration {
    const effects = this.getAllEffects();
    return {
      effects: effects.map(e => this.getEffectConfig(e.type)!).filter(Boolean)
    };
  }

  /**
   * Enable specific effects.
   */
  public enablePasses(...passes: string[]): void {
    for (const pass of passes) {
      const resolvedType = this.resolveEffectType(pass);
      if (!this.pipeline) {
        this.mergePendingConfig({ effects: [normaliseEffectConfig({ enabled: true, type: resolvedType })] });
      } else {
        this.pipeline.setEffectEnabled(resolvedType, true);
      }
    }
  }

  /**
   * Disable specific effects.
   */
  public disablePasses(...passes: string[]): void {
    for (const pass of passes) {
      const resolvedType = this.resolveEffectType(pass);
      if (!this.pipeline) {
        this.mergePendingConfig({ effects: [normaliseEffectConfig({ enabled: false, type: resolvedType })] });
      } else {
        this.pipeline.setEffectEnabled(resolvedType, false);
      }
    }
  }

  /**
   * Check if an effect is enabled.
   */
  public isPassEnabled(pass: string): boolean {
    const config = this.getEffectConfig(pass);
    return config?.enabled ?? false;
  }

  /**
   * Set whether post-processing is enabled globally.
   */
  public setPostProcessingEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.pipeline?.setEnabled(enabled);
  }

  /**
   * Get whether post-processing is enabled globally.
   */
  public getPostProcessingEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set an optional hook that runs before each render call.
   */
  public setBeforeRenderHook(hook: BeforeRenderHook | null): void {
    this.beforeRenderHook = hook;
  }

  public isWebGPU(): boolean {
    return this.pipeline?.rendererType === 'webgpu';
  }

  /**
   * Render the scene with post-processing.
   */
  @THREE.profile
  public renderScene(
    renderer: IGenesysRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    windowSize: { width: number; height: number }
  ): void {
    if (this.destroyed) {
      return;
    }

    // Initialize or switch pipeline if renderer changed
    if (this.lastRenderer !== renderer) {
      this.initializePipeline(renderer);
      this.lastRenderer = renderer;
    }

    if (this.beforeRenderHook) {
      THREE.ProfilerService.begin('PostProcessManager.renderScene.beforeRenderHook');
      try {
        this.beforeRenderHook(renderer, scene, camera, windowSize);
      } catch (error) {
        console.error('[PostProcessManager] beforeRenderHook failed:', error);
      } finally {
        THREE.ProfilerService.end('PostProcessManager.renderScene.beforeRenderHook');
      }
    }

    // Render with pipeline
    this.pipeline?.render(scene, camera, windowSize);
  }

  /**
   * Render one frame with all excluded editor objects hidden, then restore their visibility.
   *
   * Use this to capture a clean screenshot that omits editor-only objects (gizmos, grid,
   * billboards, etc.) without permanently modifying scene state. Only objects that are
   * currently visible are toggled — already-hidden objects are not affected.
   */
  public renderCleanFrame(
    renderer: IGenesysRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    windowSize: { width: number; height: number }
  ): void {
    const excludedObjects = this.getExcludedObjects();

    // Hide excluded editor objects (gizmos, grid, billboards, etc.)
    const toRestore: THREE.Object3D[] = [];
    for (const obj of excludedObjects) {
      if (obj.visible) {
        obj.visible = false;
        toRestore.push(obj);
      }
    }

    // Hide the HUD scene (viewport orientation gizmo). Setting visible=false on the
    // scene root causes the WebGPU renderer to skip _projectObject for the entire scene.
    const hudScene = this.getHudScene();
    const hudWasVisible = hudScene !== null && hudScene.visible;
    if (hudWasVisible) hudScene!.visible = false;

    try {
      this.renderScene(renderer, scene, camera, windowSize);
    } finally {
      for (const obj of toRestore) {
        obj.visible = true;
      }
      if (hudWasVisible) hudScene!.visible = true;
    }
  }

  /**
   * Clears level-specific state when reusing the manager across world transitions.
   * Keeps the GPU pipeline alive for the play session.
   */
  public resetForLevelTransition(): void {
    this.clearHiddenObjects();
    this.clearExcludedObjects();
    this.clearOutlineSelection();
    this.beforeRenderHook = null;
  }

  /**
   * Dispose of all resources.
   */
  public destroy(): void {
    this.destroyed = true;
    this.pipeline?.dispose();
    this.pipeline = null;
    this.lastRenderer = null;
    this.beforeRenderHook = null;
  }

  // ============= Hidden/Excluded Object Management =============

  /**
   * Add objects to hide from rendering entirely.
   */
  public addHiddenObjects(...objects: THREE.Object3D[]): void {
    this.pipeline?.addHiddenObjects(...objects);
  }

  /**
   * Remove objects from hidden list.
   */
  public removeHiddenObjects(...objects: THREE.Object3D[]): void {
    this.pipeline?.removeHiddenObjects(...objects);
  }

  /**
   * Clear all hidden objects.
   */
  public clearHiddenObjects(): void {
    this.pipeline?.clearHiddenObjects();
  }

  /**
   * Add objects to exclude from post-processing.
   */
  public addExcludedObjects(...objects: THREE.Object3D[]): void {
    this.pipeline?.addExcludedObjects(...objects);
  }

  /**
   * Remove objects from post-processing exclusion list.
   */
  public removeExcludedObjects(...objects: THREE.Object3D[]): void {
    this.pipeline?.removeExcludedObjects(...objects);
  }

  /**
   * Clear all post-processing excluded objects.
   */
  public clearExcludedObjects(): void {
    this.pipeline?.clearExcludedObjects();
  }

  /**
   * Set the layer used for excluded objects.
   */
  public setExcludedLayer(layer: number): void {
    this.pipeline?.setExcludedLayer(layer);
  }

  /**
   * Get the layer used for excluded objects.
   */
  public getExcludedLayer(): number {
    return this.pipeline?.getExcludedLayer() ?? 30;
  }

  /**
   * Get excluded objects set.
   */
  public getExcludedObjects(): ReadonlySet<THREE.Object3D> {
    return this.pipeline?.getExcludedObjects() ?? new Set();
  }

  // ============= HUD Compositing API =============

  /**
   * Set the HUD scene and camera for compositing on top of post-processed output.
   * Only applies to WebGPU renderer. Set both to null to disable HUD compositing.
   */
  public setHud(scene: THREE.Scene | null, camera: THREE.Camera | null): void {
    if (this.pipeline?.rendererType === 'webgpu') {
      (this.pipeline as WebGPUPipeline).setHud(scene, camera);
    }
  }

  /**
   * Return the current HUD scene if one is registered. Only WebGPU pipelines support a HUD.
   */
  public getHudScene(): THREE.Scene | null {
    if (this.pipeline?.rendererType === 'webgpu') {
      return (this.pipeline as WebGPUPipeline).getHudScene();
    }
    return null;
  }

  // ============= Outline API =============
  // These methods delegate to outline effects

  public addOutlineSelectedActors(...actors: Actor[]): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.addSelectedActors) {
      objectOutline.addSelectedActors(...actors);
    }
  }

  public addOutlineSelectedActor(actor: Actor): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.addSelectedActor) {
      objectOutline.addSelectedActor(actor);
    }
  }

  public removeOutlineSelectedActors(...actors: Actor[]): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.removeSelectedActors) {
      objectOutline.removeSelectedActors(...actors);
    }
  }

  public removeOutlineSelectedActor(actor: Actor): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.removeSelectedActor) {
      objectOutline.removeSelectedActor(actor);
    }
  }

  public clearOutlineSelection(): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.clearSelection) {
      objectOutline.clearSelection();
    }
  }

  public setOutlineSelection(actors: Actor[]): void {
    const objectOutline = this.getEffect('objectOutline') as ObjectOutlineEffect | null;
    if (objectOutline?.setSelection) {
      objectOutline.setSelection(actors);
    }
  }

  /** Check if currently using WebGPU renderer */
  public isUsingWebGPURenderer(): boolean {
    return this.pipeline?.rendererType === 'webgpu';
  }

  // ============= Private Implementation =============

  protected resolveEffectType(typeOrId: string): string {
    const normalisedType = normaliseEffectType(typeOrId);

    // Direct type match always wins.
    if (this.pipeline?.getEffect(normalisedType)) {
      return normalisedType;
    }

    // Resolve via pending config stack entry id.
    const pendingMatch = this.pendingConfig?.effects.find(effect => getEffectConfigKey(effect) === typeOrId);
    if (pendingMatch) {
      return normaliseEffectType(pendingMatch.type);
    }

    // Resolve via active pipeline config ids.
    if (this.pipeline) {
      for (const effect of this.pipeline.getAllEffects()) {
        const config = this.pipeline.getEffectConfig(effect.type);
        if (config?.id === typeOrId) {
          return effect.type;
        }
      }
    }

    return normalisedType;
  }

  /**
   * Merge configuration into pending config (used when pipeline not yet initialised).
   */
  protected mergePendingConfig(config: PostProcessConfiguration): void {
    this.pendingConfig ??= { effects: [] };
    for (const newEffect of config.effects) {
      const normalisedEffect = normaliseEffectConfig(newEffect);
      const effectKey = getEffectConfigKey(normalisedEffect);
      const existingIdx = this.pendingConfig.effects.findIndex(effect => getEffectConfigKey(effect) === effectKey);
      if (existingIdx >= 0) {
        // Merge with existing effect config
        this.pendingConfig.effects[existingIdx] = { ...this.pendingConfig.effects[existingIdx], ...normalisedEffect };
      } else {
        // Add new effect config
        this.pendingConfig.effects.push(normalisedEffect);
      }
    }
  }

  @THREE.profile
  protected initializePipeline(renderer: IGenesysRenderer): void {
    if (this.destroyed) {
      return;
    }

    // Dispose old pipeline
    this.pipeline?.dispose();

    // Create appropriate pipeline for renderer type
    const webGPURenderer = renderer.asWebGPU();
    if (webGPURenderer) {
      this.pipeline = new WebGPUPipeline(webGPURenderer);

      // Initialize async
      (this.pipeline as WebGPUPipeline).initialize().catch(error => {
        console.warn('[PostProcessManager] WebGPU pipeline initialization failed:', error);
      });
    } else {
      this.pipeline = new WebGLPipeline(renderer.asWebGL()!);
    }

    // Register built-in effects
    this.registerBuiltInEffects();

    // Apply pending config if any
    if (this.pendingConfig) {
      this.configure(this.pendingConfig);
      this.pendingConfig = undefined;
    }

    // Apply enabled state to pipeline
    this.pipeline.setEnabled(this.enabled);

    console.log(`[PostProcessManager] Initialized ${this.pipeline.rendererType} pipeline`);
  }

  protected registerBuiltInEffects(): void {
    if (!this.pipeline) return;

    // Register built-in effects in canonical stack order.
    this.pipeline.registerEffect(new AOEffect());
    this.pipeline.registerEffect(new BloomEffect());
    this.pipeline.registerEffect(new ToneMappingEffect());
    this.pipeline.registerEffect(new ColorGradingEffect());
    this.pipeline.registerEffect(new ObjectOutlineEffect());
    this.pipeline.registerEffect(new RetroEffect());
    this.pipeline.registerEffect(new PixelationEffect());
    this.pipeline.registerEffect(new DepthOfFieldEffect());
    this.pipeline.registerEffect(new SSREffect());
    this.pipeline.registerEffect(new AntiAliasingEffect());
  }
}
